import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrandMark } from '../ui/BrandMark';
import { LanguageToggle } from '../ui/LanguageToggle';
import { useLanguage } from '../../contexts/LanguageContext';
import { AUTH_PATHS } from '../auth/authPaths';
import './landing.css';

const Reveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => node.classList.add('is-in'), delay);
            observer.unobserve(node);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className="qp-reveal">
      {children}
    </div>
  );
};

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const goRegister = () => navigate(AUTH_PATHS.register);
  const goLogin = () => navigate(AUTH_PATHS.login);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="qp-landing">
      <div className="qp-grain" aria-hidden />

      <nav className="qp-nav">
        <span className="qp-wordmark">
          <BrandMark className="qp-wordmark-mark" />
          {t.common.brandName}
        </span>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <LanguageToggle />
          <a
            href="tel:+77772468483"
            className="qp-nav-link"
            style={{ textDecoration: 'none' }}
            aria-label={t.common.supportPhoneLabel}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M3 3.5A1.5 1.5 0 014.5 2h1.65a1 1 0 01.97.757l.6 2.4a1 1 0 01-.27.95L6.3 7.26a8 8 0 003.44 3.44l1.15-1.15a1 1 0 01.95-.27l2.4.6a1 1 0 01.76.97V12.5A1.5 1.5 0 0113.5 14a11 11 0 01-10.5-10.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
            <span className="qp-nav-phone-full">+7 777 246 84 83</span>
            <span className="qp-nav-phone-short">{t.landing.support}</span>
          </a>
          <button type="button" className="qp-nav-link" onClick={goLogin}>
            {t.landing.login}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </nav>

      <section className="qp-hero">
        <div className="qp-mesh" aria-hidden />

        <span className="qp-hero-eyebrow">
          <span className="qp-hero-eyebrow-pulse" aria-hidden />
          {t.landing.eyebrow}
        </span>

        <h1 className="qp-hero-title">
          <span className="strike">{t.landing.heroTitleStrike}</span>
          <br />
          <span className="accent">{t.landing.heroTitleAccent}</span>
        </h1>

        <p className="qp-hero-sub">
          {t.landing.heroSub}
        </p>

        <div className="qp-hero-cta">
          <button type="button" className="qp-btn-primary" onClick={goRegister}>
            {t.landing.becomePartner}
            <svg className="arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M3 8h10M8 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button type="button" className="qp-btn-ghost" onClick={() => scrollTo('how')}>
            {t.landing.howItWorks}
          </button>
          <div className="qp-hero-meta">
            <span className="qp-hero-meta-dot" aria-hidden />
            {t.landing.heroMeta}
          </div>
        </div>
      </section>

      <div className="qp-showcase">
        <div className="qp-showcase-frame">
          <p className="qp-showcase-label">{t.landing.showcaseLabel}</p>
          <div className="qp-showcase-inner">
            <div className="qp-showcase-copy">
              <h3>
                {t.landing.showcaseTitle}
              </h3>
              <p>
                {t.landing.showcaseDesc}
              </p>
            </div>

            <div className="qp-cards-stack" aria-hidden>
              <div className="qp-float qp-float-1">
                <div className="qp-card-row">
                  <span className="qp-card-emoji">💇</span>
                  <div>
                    <div className="qp-card-title">{t.landing.cardHaircut}</div>
                    <div className="qp-card-meta">{t.landing.cardHaircutMeta}</div>
                  </div>
                  <span className="qp-chip-discount">−35%</span>
                </div>
                <div className="qp-card-price">
                  <span className="qp-price-old">12 000 ₸</span>
                  <span className="qp-price-new">7 800 ₸</span>
                </div>
              </div>

              <div className="qp-float qp-float-2">
                <div className="qp-card-row">
                  <span className="qp-card-emoji">🏋️</span>
                  <div>
                    <div className="qp-card-title">{t.landing.cardFitness}</div>
                    <div className="qp-card-meta">{t.landing.cardFitnessMeta}</div>
                  </div>
                  <span className="qp-chip-discount">−25%</span>
                </div>
                <div className="qp-card-price">
                  <span className="qp-price-old">9 000 ₸</span>
                  <span className="qp-price-new">6 750 ₸</span>
                </div>
              </div>

              <div className="qp-float qp-float-3">
                <div className="qp-card-row">
                  <span className="qp-card-emoji">☕</span>
                  <div>
                    <div className="qp-card-title">{t.landing.cardCoffee}</div>
                    <div className="qp-card-meta">{t.landing.cardCoffeeMeta}</div>
                  </div>
                  <span className="qp-chip-discount">−50%</span>
                </div>
                <div className="qp-card-price">
                  <span className="qp-price-old">2 200 ₸</span>
                  <span className="qp-price-new">1 100 ₸</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="qp-stats">
        <Reveal>
          <div>
            <div className="qp-stat-num">
              10<span className="accent">k+</span>
            </div>
            <div className="qp-stat-label">{t.landing.statsUsers}</div>
          </div>
        </Reveal>
        <Reveal delay={80}>
          <div>
            <div className="qp-stat-num">
              200<span className="accent">+</span>
            </div>
            <div className="qp-stat-label">{t.landing.statsVenues}</div>
          </div>
        </Reveal>
        <Reveal delay={160}>
          <div>
            <div className="qp-stat-num">
              3<span className="accent">мин</span>
            </div>
            <div className="qp-stat-label">{t.landing.statsSpeed}</div>
          </div>
        </Reveal>
        <Reveal delay={240}>
          <div>
            <div className="qp-stat-num">
              0<span className="accent">%</span>
            </div>
            <div className="qp-stat-label">{t.landing.statsCommission}</div>
          </div>
        </Reveal>
      </div>

      <section className="qp-section" id="how">
        <div className="qp-section-head">
          <div>
            <p className="qp-section-kicker">{t.landing.howTitle}</p>
            <h2 className="qp-section-title">
              {t.landing.howSubtitle}
            </h2>
          </div>
        </div>

        <div className="qp-steps">
          <Reveal>
            <div className="qp-step">
              <div className="qp-step-num">{t.landing.step1Number}</div>
              <h3 className="qp-step-title">{t.landing.step1Title}</h3>
              <p className="qp-step-text">
                {t.landing.step1Desc}
              </p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="qp-step">
              <div className="qp-step-num">{t.landing.step2Number}</div>
              <h3 className="qp-step-title">{t.landing.step2Title}</h3>
              <p className="qp-step-text">
                {t.landing.step2Desc}
              </p>
            </div>
          </Reveal>
          <Reveal delay={240}>
            <div className="qp-step">
              <div className="qp-step-num">{t.landing.step3Number}</div>
              <h3 className="qp-step-title">{t.landing.step3Title}</h3>
              <p className="qp-step-text">
                {t.landing.step3Desc}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="qp-section">
        <div className="qp-section-head">
          <div>
            <p className="qp-section-kicker">{t.landing.featuresTitle}</p>
            <h2 className="qp-section-title">
              {t.landing.featuresSubtitle}
            </h2>
          </div>
        </div>

        <div className="qp-benefits">
          <Reveal>
            <div className="qp-benefit qp-benefit-a">
              <div>
                <p className="qp-benefit-label">{t.landing.feat1Title}</p>
                <h3 className="qp-benefit-headline">
                  {t.landing.feat1Desc}
                </h3>
              </div>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <div className="qp-benefit qp-benefit-b">
              <p className="qp-benefit-label">{t.landing.feat2Title}</p>
              <h3 className="qp-benefit-headline">{t.landing.feat2Desc}</h3>
            </div>
          </Reveal>

          <Reveal delay={160}>
            <div className="qp-benefit qp-benefit-c">
              <p className="qp-benefit-label">{t.landing.feat3Title}</p>
              <h3 className="qp-benefit-headline">{t.landing.feat3Desc}</h3>
            </div>
          </Reveal>

          <Reveal delay={240}>
            <div className="qp-benefit qp-benefit-d">
              <p className="qp-benefit-label">{t.landing.feat4Title}</p>
              <h3 className="qp-benefit-headline">{t.landing.feat4Desc}</h3>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="qp-section">
        <Reveal>
          <div className="qp-quote">
            <div className="qp-quote-mark">“</div>
            <p className="qp-quote-text">
              {t.landing.quoteText}
            </p>
            <div className="qp-quote-author">
              <span className="qp-quote-avatar">Д</span>
              <div>
                <div className="qp-quote-author-name">{t.landing.quoteAuthor}</div>
                <div className="qp-quote-author-role">{t.landing.quoteRole}</div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="qp-final">
        <h2>{t.landing.ctaTitle}</h2>
        <p>
          {t.landing.ctaSub}
        </p>
        <div className="qp-hero-cta">
          <button type="button" className="qp-btn-primary" onClick={goRegister}>
            {t.landing.ctaButton}
            <svg className="arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M3 8h10M8 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <a href="tel:+77772468483" className="qp-btn-ghost" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M3 3.5A1.5 1.5 0 014.5 2h1.65a1 1 0 01.97.757l.6 2.4a1 1 0 01-.27.95L6.3 7.26a8 8 0 003.44 3.44l1.15-1.15a1 1 0 01.95-.27l2.4.6a1 1 0 01.76.97V12.5A1.5 1.5 0 0113.5 14a11 11 0 01-10.5-10.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
            {t.common.support} · +7 777 246 84 83
          </a>
        </div>
      </section>

      <footer className="qp-footer">
        <span>{t.landing.footerCopyright} {new Date().getFullYear()} · {t.landing.footerCities}</span>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <Link to="/support" style={{ color: 'var(--qp-ink-soft)', textDecoration: 'none' }}>
            {t.landing.footerSupport}
          </Link>
          <Link to="/terms/clients" style={{ color: 'var(--qp-ink-muted)', textDecoration: 'none' }}>
            {t.landing.footerClients}
          </Link>
          <Link to="/terms/partners" style={{ color: 'var(--qp-ink-muted)', textDecoration: 'none' }}>
            {t.landing.footerPartners}
          </Link>
          <Link to="/ugc-rules" style={{ color: 'var(--qp-ink-muted)', textDecoration: 'none' }}>
            {t.landing.footerUgc}
          </Link>
          <Link to="/privacy" style={{ color: 'var(--qp-ink-muted)', textDecoration: 'none' }}>
            {t.landing.footerPrivacy}
          </Link>
          <a href="tel:+77772468483" style={{ color: 'var(--qp-ink-soft)', textDecoration: 'none' }}>
            +7 777 246 84 83
          </a>
          <a href="mailto:partner@qprice.kz" style={{ color: 'var(--qp-ink-muted)', textDecoration: 'none' }}>
            partner@qprice.kz
          </a>
        </div>
      </footer>
    </div>
  );
};
